import { useEffect } from 'react';
import type { HydrogenFcfsRoot } from 'src/apis/hydrogen-fcfs/types';
import { useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';
import { handleError } from 'src/utils/handle-error';
import { reportToSentry } from 'src/utils/report-to-sentry';

export const hydrogenFcfsRootCacheKey = 'hydrogen-fcfs-root';

export function useHydrogenFcfsRoot() {
  const request = useAxios(
    (axios, baseConfig) =>
      axios.request<HydrogenFcfsRoot>({
        ...baseConfig,
        url: `${PRISMA_CONFIG.hydrogenFcfs}`,
        timeout: 5000,
      }),
    {
      neededOnPageLoad: true,
      cacheKey: hydrogenFcfsRootCacheKey,
      onError(error) {
        const [_, report] = handleError(error, () => {});
        if (report) reportToSentry(error);
        return error;
      },
    }
  );

  useEffect(() => {
    if (!PRISMA_CONFIG.hydrogenFcfs) return;
    request.execute();
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  if (!PRISMA_CONFIG.hydrogenFcfs) return;

  return request;
}

export type HydrogenFcfsRootRequest = ReturnType<typeof useHydrogenFcfsRoot>;

/**
 * @throws {Forbidden} if the link is not found after root is loaded
 * @returns `undefined` during loading, the link's href otherwise
 */
export function useHydrogenFcfsRootLink(
  link: keyof NonNullable<HydrogenFcfsRoot['_links']>
) {
  const root = useHydrogenFcfsRoot();
  const data = root?.response?.data;
  if (data && !data._links?.[link]) throw new Forbidden();

  return data?._links?.[link]?.href;
}

/**
 * @returns `undefined` during loading, the link's href or `null` otherwise
 */
export function useHydrogenFcfsRootLinkOptional(
  link: keyof NonNullable<HydrogenFcfsRoot['_links']>
) {
  const root = useHydrogenFcfsRoot();
  const data = root?.response?.data;
  if (!data) return undefined;

  return data._links?.[link]?.href ?? null;
}
